import React from "react";
import { Link, Rating, Typography } from "@mui/material";
import type { GoogleRating } from "../../hooks/useGoogleRating";

const GoogleG: React.FC<{ size?: number }> = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden focusable="false">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

interface GoogleRatingBadgeProps {
  data: GoogleRating | null;
  /** dark = on navy/gradient backgrounds */
  tone?: "dark" | "light";
}

/** Live Google rating pill — renders nothing until rating data is available. */
const GoogleRatingBadge: React.FC<GoogleRatingBadgeProps> = ({ data, tone = "light" }) => {
  if (!data || typeof data.rating !== "number") return null;

  const isDark = tone === "dark";

  return (
    <Link
      href={data.mapsUrl || "https://www.google.com/maps/search/R+S+Enterprises+Laxmi+Nagar+Delhi"}
      target="_blank"
      rel="noopener noreferrer"
      underline="none"
      aria-label={`Rated ${data.rating} out of 5 on Google from ${data.total} reviews — view on Google Maps`}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 1.2,
        px: 2,
        py: 1,
        borderRadius: "999px",
        border: "1px solid",
        borderColor: isDark ? "rgba(255,255,255,0.25)" : "divider",
        bgcolor: isDark ? "rgba(255,255,255,0.08)" : "background.paper",
        backdropFilter: isDark ? "blur(10px)" : "none",
        transition: "transform 0.2s ease",
        "&:hover": { transform: "translateY(-2px)" },
      }}
    >
      <GoogleG />
      <Rating value={data.rating} precision={0.1} readOnly size="small" />
      <Typography
        variant="body2"
        sx={{ fontWeight: 700, color: isDark ? "common.white" : "text.primary" }}
      >
        {data.rating.toFixed(1)}
      </Typography>
      <Typography
        variant="body2"
        sx={{ color: isDark ? "rgba(255,255,255,0.75)" : "text.secondary" }}
      >
        · {data.total} reviews
      </Typography>
    </Link>
  );
};

export default GoogleRatingBadge;
